import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Navigate, redirect, useRouterState } from '@tanstack/react-router';
import { z } from 'zod';
import { CrmPrimaryNavigation } from '@pages/crm';
import { useUserProfileContext } from '@entities/user-profile';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import {
  HelpdeskDetail,
  helpdeskDetailFromListQuery,
  helpdeskThreadQuery,
  type HelpdeskThread,
  helpdeskDetailSearchSchema,
  getHelpdeskListReturnSearch,
} from '@features/helpdesk';
import { isApiError } from '@shared/api';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessHelpdesk,
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
  canAccessCrmCustomerProfile,
} from '@shared/lib';

export const Route = createFileRoute('/$locale/_authenticated/crm/helpdesk/$taskUuid')({
  params: z.object({ taskUuid: z.uuid() }),
  validateSearch: helpdeskDetailSearchSchema,
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessHelpdesk(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: HelpdeskDetailRoute,
});

function HelpdeskDetailRoute() {
  const { locale, taskUuid } = Route.useParams();
  const search = Route.useSearch();
  const currentLocation = useRouterState({ select: (state) => state.location.href });
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const { user, permissions } = useAuth();
  const profileContext = useUserProfileContext(user?.id ?? '');
  const canRead = canAccessHelpdesk(ability);
  const client = useQueryClient();
  const viewerAccessScope = user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : '';
  const activeTab = search.helpdeskTab ?? 'overview';
  const [batch, setBatch] = useState({
    scope: viewerAccessScope,
    taskUuid,
    page: 1,
    reauthorizing: false,
  });
  const batchPage =
    batch.scope === viewerAccessScope && batch.taskUuid === taskUuid ? batch.page : 1;
  const thread = useQuery({
    ...helpdeskThreadQuery(viewerAccessScope, taskUuid, {
      page: batchPage,
      pageSize: 20,
    }),
    enabled: canRead && activeTab === 'conversation',
    placeholderData: (previous, previousQuery) =>
      previous?.task.id === taskUuid && previousQuery?.queryKey[1] === viewerAccessScope
        ? previous
        : undefined,
  });
  const query = useQuery({
    ...helpdeskDetailFromListQuery(
      client,
      user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : '',
      taskUuid
    ),
    enabled: canRead,
  });
  const deletion = useRef({ viewerAccessScope, taskUuid, pending: false, finished: false });
  if (
    deletion.current.viewerAccessScope !== viewerAccessScope ||
    deletion.current.taskUuid !== taskUuid
  )
    deletion.current = { viewerAccessScope, taskUuid, pending: false, finished: false };
  if (
    query.data?.data.deletionState?.status === 'pending' ||
    thread.data?.task.deletionState?.status === 'pending'
  )
    deletion.current.pending = true;
  useEffect(() => {
    const error = activeTab === 'conversation' ? thread.error : query.error;
    if (
      !deletion.current.pending ||
      deletion.current.finished ||
      !isApiError(error) ||
      error.status !== 404
    )
      return;
    deletion.current.finished = true;
    // A pending resource is now unavailable. Reauthorize the list rather than retaining its old row.
    void client.invalidateQueries({
      queryKey: ['helpdesk', viewerAccessScope],
      refetchType: 'none',
    });
    if (search.helpdeskCustomerId && canAccessCrmCustomerProfile(ability)) {
      void navigate({
        to: '/$locale/crm/user-profiles/$userProfileId/helpdesk',
        params: { locale, userProfileId: search.helpdeskCustomerId },
        search: getHelpdeskListReturnSearch(search),
        replace: true,
      });
    } else {
      void navigate({
        to: '/$locale/crm/helpdesk',
        params: { locale },
        search: getHelpdeskListReturnSearch(search),
        replace: true,
      });
    }
  }, [
    ability,
    activeTab,
    client,
    locale,
    navigate,
    query.error,
    search,
    thread.error,
    viewerAccessScope,
  ]);
  const denied = isApiError(thread.error) && [401, 403, 404].includes(thread.error.status);
  const cachedThreadTask = client
    .getQueriesData<HelpdeskThread>({
      queryKey: ['helpdesk', viewerAccessScope, 'thread', taskUuid],
    })
    .find(([, cached]) => cached?.task.id === taskUuid)?.[1]?.task;
  const recovering =
    batch.scope === viewerAccessScope && batch.taskUuid === taskUuid && batch.reauthorizing;
  useEffect(() => {
    if (recovering && thread.isSuccess && !thread.isFetching && !thread.isPlaceholderData) {
      setBatch((current) =>
        current.scope === viewerAccessScope && current.taskUuid === taskUuid
          ? { ...current, reauthorizing: false }
          : current
      );
    }
  }, [
    recovering,
    thread.isSuccess,
    thread.isFetching,
    thread.isPlaceholderData,
    viewerAccessScope,
    taskUuid,
  ]);
  const conversationTask =
    denied || recovering ? undefined : (thread.data?.task ?? cachedThreadTask ?? query.data?.data);
  if (!canRead) return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmPrimaryNavigation
        activeSection="helpdesk"
        canAccessHelpdesk
        canAccessProfiles={canAccessCrmCustomerDirectory(ability)}
        canAccessMemberships={canAccessCrmCustomerMembershipDirectory(ability)}
        locale={locale}
      />
      <HelpdeskDetail
        taskUuid={taskUuid}
        emailChannel={query.data?.data.emailChannel}
        viewerProfileId={profileContext.data?.data.profile_ref}
        canCreate={ability.can('create', 'HelpdeskTask')}
        canDelete={ability.can('delete', 'HelpdeskTask')}
        canUpdate={ability.can('update', 'HelpdeskTask')}
        canGenerateArticle={
          ability.can('update', 'HelpdeskTask') &&
          ability.can('read', 'HelpdeskArticle') &&
          ability.can('create', 'HelpdeskArticle') &&
          ability.can('update', 'HelpdeskArticle')
        }
        linkedArticle={query.data?.data.linkedArticle}
        onOpenArticle={
          ability.can('read', 'HelpdeskArticle')
            ? (articleId) => {
                void navigate({
                  to: '/$locale/crm/helpdesk/articles/$articleId',
                  params: { locale, articleId },
                  search: { ...getHelpdeskListReturnSearch(search), articleTaskUuid: taskUuid },
                });
              }
            : undefined
        }
        viewerAccessScope={user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : ''}
        task={
          activeTab === 'conversation' ? conversationTask : (query.data?.data ?? thread.data?.task)
        }
        isLoading={
          activeTab === 'conversation'
            ? (thread.isLoading && !conversationTask) || (recovering && !thread.isError)
            : query.isLoading && !thread.data
        }
        error={
          activeTab === 'conversation'
            ? !conversationTask || denied
              ? thread.error
              : null
            : query.error
        }
        activeTab={activeTab}
        replyPage={batchPage}
        replyPageSize={20}
        threadQuery={thread}
        onTabChange={(tab) => {
          void navigate({ search: { ...search, helpdeskTab: tab } });
        }}
        onPaginationChange={(page) => {
          setBatch({ scope: viewerAccessScope, taskUuid, page, reauthorizing: false });
        }}
        onRetry={() => {
          if (activeTab === 'conversation') {
            if (denied) {
              void client.invalidateQueries({
                queryKey: helpdeskThreadQuery(viewerAccessScope, taskUuid, {
                  page: 1,
                  pageSize: 20,
                }).queryKey,
                exact: true,
                refetchType: 'none',
              });
              setBatch({ scope: viewerAccessScope, taskUuid, page: 1, reauthorizing: true });
              if (batchPage === 1) void thread.refetch();
            } else void thread.refetch();
          } else void query.refetch();
        }}
        onBack={() => {
          if (search.helpdeskCustomerId && canAccessCrmCustomerProfile(ability)) {
            void navigate({
              to: '/$locale/crm/user-profiles/$userProfileId/helpdesk',
              params: { locale, userProfileId: search.helpdeskCustomerId },
              search: getHelpdeskListReturnSearch(search),
            });
            return;
          }
          void navigate({
            to: '/$locale/crm/helpdesk',
            params: { locale },
            search: getHelpdeskListReturnSearch(search),
          });
        }}
        onOpenCustomer={
          canAccessCrmCustomerProfile(ability)
            ? (customerId) => {
                void navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId',
                  params: { locale, userProfileId: customerId },
                  search: { helpdeskReturnTo: currentLocation },
                });
              }
            : undefined
        }
      />
    </main>
  );
}
